import { Trans, useTranslation } from "react-i18next";
import { useFormat, type Formatters } from "../../i18n/format";
import type { Translate } from "../../i18n/translate";
import { errorText } from "../../utils/apiError";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import CodeIcon from "@mui/icons-material/Code";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type Hls from "hls.js";
import { PageContainer, PageHeader } from "../../components/PageHeader";
import { showcaseApi, type ShowcaseConfig } from "../../http/showcase";
import {
  chaptersToText,
  embedSnippet,
  formatTimecode,
  normaliseRef,
  parseChapters,
  rewatchedRanges,
  SHOWCASE_NOTIFY_COMPLETION_PCT,
  type ShowcaseAnalytics,
  type ShowcaseVideo,
  type ShowcaseVideoPatch,
} from "@shared/showcase";
import { posterFromElement } from "./masterUpload";
import { cancelUpload, startUpload, useUploads } from "./uploadManager";
import { statusChip } from "./ShowcasePage";
import WatchHeatmap from "./WatchHeatmap";
import CaptionsTab from "./CaptionsTab";
import { FONT_MONO } from "../../theme";

const EMPTY = "—";

/** A plain HLS preview for the CMS: native controls are fine for staff. */
function Preview({
  src,
  poster,
  onVideo,
}: {
  src: string;
  poster: string | null;
  onVideo: (v: HTMLVideoElement | null) => void;
}) {
  const el = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = el.current;
    onVideo(v);
    if (!v) return;
    let h: Hls | null = null;
    let cancelled = false;
    if (v.canPlayType("application/vnd.apple.mpegurl")) {
      v.src = src;
    } else {
      void import("hls.js").then(({ default: HlsCtor }) => {
        if (cancelled) return;
        // No worker: the CMS CSP has no worker-src (src/shared/csp.ts).
        h = new HlsCtor({ enableWorker: false });
        h.loadSource(src);
        h.attachMedia(v);
      });
    }
    return () => {
      cancelled = true;
      h?.destroy();
    };
  }, [src, onVideo]);
  return (
    <video
      ref={el}
      controls
      playsInline
      crossOrigin="anonymous"
      poster={poster ?? undefined}
      style={{
        width: "100%",
        borderRadius: 12,
        background: "#000",
        display: "block",
      }}
    />
  );
}

function Kpi({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Card variant="outlined" sx={{ flex: 1, minWidth: 150, borderRadius: 3 }}>
      <CardContent>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ typography: "eyebrow" }}
        >
          {label}
        </Typography>
        <Typography variant="h4" fontWeight="fontWeightBold" sx={{ mt: 0.5 }}>
          {value}
        </Typography>
        {hint && (
          <Typography variant="body2" color="text.secondary">
            {hint}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function timeAgo(t: Translate, format: Formatters, iso: string): string {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 90) return t("users:showcaseVideoPage.timeAgo.justNow");
  if (s < 3600)
    return t("users:showcaseVideoPage.timeAgo.minutes", {
      count: Math.round(s / 60),
    });
  if (s < 86400)
    return t("users:showcaseVideoPage.timeAgo.hours", {
      count: Math.round(s / 3600),
    });
  return format.date(iso);
}

function AnalyticsTab({ video }: { video: ShowcaseVideo }) {
  const { t } = useTranslation(["users", "common"]);
  const format = useFormat();
  const [a, setA] = useState<ShowcaseAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    const load = () =>
      showcaseApi
        .analytics(video.id)
        .then((r) => live && setA(r))
        .catch((e) => live && setError(errorText(e, t)));
    void load();
    const timer = window.setInterval(load, 15_000);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [video.id, t]);

  const summary = useMemo(() => {
    if (!a) return null;
    const n = a.sessions.length;
    const avg = n
      ? Math.round(a.sessions.reduce((s, x) => s + x.completionPct, 0) / n)
      : 0;
    const finished = a.sessions.filter(
      (s) => s.completionPct >= SHOWCASE_NOTIFY_COMPLETION_PCT
    ).length;
    const orgs = new Set(a.sessions.map((s) => s.organisation).filter(Boolean))
      .size;
    // Longest stretch rewatched by anyone, from the rolled-up curve.
    const bits = new Uint8Array(Math.ceil(a.heatmap.rewatches.length / 8));
    a.heatmap.rewatches.forEach((c, i) => {
      if (c > 0) bits[i >> 3] |= 1 << (i & 7);
    });
    const hot = rewatchedRanges(bits)[0] ?? null;
    return { n, avg, finished, orgs, hot };
  }, [a]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!a || !summary) return <LinearProgress />;

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <Kpi
          label={t("showcaseVideoPage.analytics.views")}
          value={String(summary.n)}
          hint={
            summary.orgs
              ? t("showcaseVideoPage.analytics.fromNetworks", {
                  count: summary.orgs,
                })
              : undefined
          }
        />
        <Kpi
          label={t("showcaseVideoPage.analytics.avgWatched")}
          value={`${summary.avg}%`}
        />
        <Kpi
          label={t("showcaseVideoPage.analytics.watchedAtLeast", {
            pct: SHOWCASE_NOTIFY_COMPLETION_PCT,
          })}
          value={String(summary.finished)}
        />
        <Kpi
          label={t("showcaseVideoPage.analytics.mostRewatched")}
          value={
            summary.hot
              ? formatTimecode(summary.hot.from)
              : t("common:labels.emptyValue")
          }
          hint={
            summary.hot
              ? t("showcaseVideoPage.analytics.toTime", {
                  time: formatTimecode(summary.hot.to),
                })
              : t("showcaseVideoPage.analytics.nothingYet")
          }
        />
      </Stack>

      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography fontWeight="fontWeightSemibold" sx={{ mb: 1.5 }}>
            {t("showcaseVideoPage.analytics.wherePeopleWatched")}
          </Typography>
          <WatchHeatmap
            views={a.heatmap.views}
            rewatches={a.heatmap.rewatches}
            chapters={video.chapters}
          />
        </CardContent>
      </Card>

      {a.refs.length > 0 && (
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography fontWeight="fontWeightSemibold">
              {t("showcaseVideoPage.analytics.taggedLinks")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <Trans
                t={t}
                i18nKey="showcaseVideoPage.analytics.taggedLinksHint"
                components={{ code: <code /> }}
              />
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    {t("showcaseVideoPage.analytics.columns.ref")}
                  </TableCell>
                  <TableCell align="right">
                    {t("showcaseVideoPage.analytics.views")}
                  </TableCell>
                  <TableCell align="right">
                    {t("showcaseVideoPage.analytics.columns.bestWatched")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {a.refs.map((r) => (
                  <TableRow key={r.ref}>
                    <TableCell>{r.ref}</TableCell>
                    <TableCell align="right">{r.sessions}</TableCell>
                    <TableCell align="right">{r.bestCompletionPct}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography fontWeight="fontWeightSemibold">
            {t("showcaseVideoPage.analytics.views")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("showcaseVideoPage.analytics.networkHint")}
          </Typography>
          {a.sessions.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              {t("showcaseVideoPage.analytics.noViews")}
            </Typography>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("showcaseVideoPage.analytics.columns.when")}
                    </TableCell>
                    <TableCell>
                      {t("showcaseVideoPage.analytics.columns.network")}
                    </TableCell>
                    <TableCell>
                      {t("showcaseVideoPage.analytics.columns.where")}
                    </TableCell>
                    <TableCell>
                      {t("showcaseVideoPage.analytics.columns.ref")}
                    </TableCell>
                    <TableCell>
                      {t("showcaseVideoPage.analytics.columns.device")}
                    </TableCell>
                    <TableCell align="right">
                      {t("showcaseVideoPage.analytics.columns.watched")}
                    </TableCell>
                    <TableCell align="right">
                      {t("showcaseVideoPage.analytics.columns.furthest")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {a.sessions.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>{timeAgo(t, format, s.lastSeenAt)}</TableCell>
                      <TableCell>{s.organisation ?? EMPTY}</TableCell>
                      <TableCell>
                        {[s.city, s.country].filter(Boolean).join(", ") ||
                          EMPTY}
                      </TableCell>
                      <TableCell>{s.ref ?? EMPTY}</TableCell>
                      <TableCell>{s.device ?? EMPTY}</TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="flex-end"
                        >
                          <LinearProgress
                            variant="determinate"
                            value={s.completionPct}
                            sx={{ width: 60, borderRadius: 2 }}
                          />
                          <span>{s.completionPct}%</span>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        {formatTimecode(s.maxPositionSeconds)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}

/**
 * "Copy embed code": the iframe snippet for a website, with a ref label so
 * website views stay apart from sales links in the analytics. The snippet
 * itself is built by `embedSnippet` (@shared/showcase), which escapes it.
 */
export function EmbedCode({
  video,
  baseUrl,
}: {
  video: ShowcaseVideo;
  baseUrl: string;
}) {
  const { t } = useTranslation("users");
  const [ref, setRef] = useState("website");
  const [copied, setCopied] = useState<"ok" | "failed" | null>(null);
  const cleanRef = normaliseRef(ref);
  const snippet = embedSnippet({
    baseUrl,
    slug: video.slug,
    title: video.title,
    ref: cleanRef,
    width: video.width,
    height: video.height,
  });
  return (
    <Stack spacing={1}>
      <Typography variant="body2" fontWeight="fontWeightSemibold">
        {t("showcaseVideoPage.embed.heading")}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          size="small"
          label={t("showcaseVideoPage.embed.label")}
          value={ref}
          onChange={(e) => {
            setRef(e.target.value);
            setCopied(null);
          }}
          error={ref.trim() !== "" && !cleanRef}
          helperText={
            ref.trim() !== "" && !cleanRef
              ? t("showcaseVideoPage.embed.invalid")
              : t("showcaseVideoPage.embed.example")
          }
          sx={{ flex: 1 }}
          inputProps={{ "data-testid": "embed-ref" }}
        />
        <Button
          variant="outlined"
          startIcon={<CodeIcon />}
          data-testid="copy-embed"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(snippet);
              setCopied("ok");
            } catch {
              setCopied("failed");
            }
          }}
          sx={{ whiteSpace: "nowrap", mt: "1px" }}
        >
          {copied === "ok"
            ? t("showcaseVideoPage.embed.copied")
            : t("showcaseVideoPage.embed.copy")}
        </Button>
      </Stack>
      {copied === "failed" && (
        <TextField
          value={snippet}
          multiline
          size="small"
          InputProps={{ readOnly: true }}
          helperText={t("showcaseVideoPage.embed.clipboardBlocked")}
          onFocus={(e) => e.target.select()}
        />
      )}
      {!video.published && (
        <Typography variant="caption" color="warning.main">
          {t("showcaseVideoPage.embed.onlyWhileLive")}
        </Typography>
      )}
    </Stack>
  );
}

interface Draft {
  title: string;
  pitch: string;
  description: string;
  accentColor: string;
  ctaLabel: string;
  ctaUrl: string;
  presenterName: string;
  presenterRole: string;
  presenterEmail: string;
  chapters: string;
  notifyEmails: string;
}

function draftOf(v: ShowcaseVideo): Draft {
  return {
    title: v.title,
    pitch: v.pitch,
    description: v.description,
    accentColor: v.accentColor,
    ctaLabel: v.cta?.label ?? "",
    ctaUrl: v.cta?.url ?? "",
    presenterName: v.presenter?.name ?? "",
    presenterRole: v.presenter?.role ?? "",
    presenterEmail: v.presenter?.email ?? "",
    chapters: chaptersToText(v.chapters),
    notifyEmails: v.notifyEmails.join(", "),
  };
}

function patchOf(d: Draft): ShowcaseVideoPatch {
  return {
    title: d.title,
    pitch: d.pitch,
    description: d.description,
    accentColor: d.accentColor,
    cta:
      d.ctaLabel.trim() && d.ctaUrl.trim()
        ? { label: d.ctaLabel.trim(), url: d.ctaUrl.trim() }
        : null,
    presenter: d.presenterName.trim()
      ? {
          name: d.presenterName.trim(),
          ...(d.presenterRole.trim() ? { role: d.presenterRole.trim() } : {}),
          ...(d.presenterEmail.trim()
            ? { email: d.presenterEmail.trim() }
            : {}),
        }
      : null,
    chapters: parseChapters(d.chapters),
    notifyEmails: d.notifyEmails
      .split(/[,\s]+/)
      .map((e) => e.trim())
      .filter(Boolean),
  };
}

function DetailsTab({
  video,
  onSaved,
}: {
  video: ShowcaseVideo;
  onSaved: (v: ShowcaseVideo) => void;
}) {
  const { t } = useTranslation(["users", "common"]);
  const [d, setD] = useState<Draft>(() => draftOf(video));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => setD(draftOf(video)), [video.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty =
    JSON.stringify(patchOf(d)) !== JSON.stringify(patchOf(draftOf(video)));
  const set = (k: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setSaved(false);
    setD({ ...d, [k]: e.target.value });
  };

  return (
    <Stack spacing={2.5}>
      <TextField
        label={t("showcaseVideoPage.details.title")}
        value={d.title}
        onChange={set("title")}
        inputProps={{ maxLength: 140 }}
      />
      <TextField
        label={t("showcaseVideoPage.details.pitch")}
        helperText={t("showcaseVideoPage.details.pitchHelp")}
        value={d.pitch}
        onChange={set("pitch")}
        inputProps={{ maxLength: 280 }}
      />
      <TextField
        label={t("showcaseVideoPage.details.about")}
        multiline
        minRows={3}
        value={d.description}
        onChange={set("description")}
        inputProps={{ maxLength: 4000 }}
      />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          label={t("showcaseVideoPage.details.accentColour")}
          value={d.accentColor}
          onChange={set("accentColor")}
          sx={{ maxWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <input
                  type="color"
                  value={
                    /^#[0-9a-f]{6}$/i.test(d.accentColor)
                      ? d.accentColor
                      : "#7c9cff"
                  }
                  onChange={(e) => setD({ ...d, accentColor: e.target.value })}
                  style={{
                    width: 28,
                    height: 28,
                    border: 0,
                    padding: 0,
                    background: "none",
                  }}
                  aria-label={t("showcaseVideoPage.details.pickAccent")}
                />
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      <Typography fontWeight="fontWeightSemibold" sx={{ pt: 1 }}>
        {t("showcaseVideoPage.details.cta")}
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          label={t("showcaseVideoPage.details.buttonLabel")}
          placeholder={t("showcaseVideoPage.details.ctaPlaceholder")}
          value={d.ctaLabel}
          onChange={set("ctaLabel")}
          sx={{ flex: 1 }}
        />
        <TextField
          label={t("showcaseVideoPage.details.link")}
          placeholder={t("showcaseVideoPage.details.linkPlaceholder")}
          value={d.ctaUrl}
          onChange={set("ctaUrl")}
          sx={{ flex: 2 }}
        />
      </Stack>
      <Typography fontWeight="fontWeightSemibold" sx={{ pt: 1 }}>
        {t("showcaseVideoPage.details.presenter")}
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          label={t("showcaseVideoPage.details.name")}
          value={d.presenterName}
          onChange={set("presenterName")}
          sx={{ flex: 1 }}
        />
        <TextField
          label={t("showcaseVideoPage.details.role")}
          value={d.presenterRole}
          onChange={set("presenterRole")}
          sx={{ flex: 1 }}
        />
        <TextField
          label={t("showcaseVideoPage.details.emailPrivate")}
          value={d.presenterEmail}
          onChange={set("presenterEmail")}
          sx={{ flex: 1 }}
        />
      </Stack>
      <TextField
        label={t("showcaseVideoPage.details.chapters")}
        multiline
        minRows={4}
        value={d.chapters}
        onChange={set("chapters")}
        placeholder={t("showcaseVideoPage.details.chaptersPlaceholder")}
        helperText={t("showcaseVideoPage.details.chaptersHelp")}
        inputProps={{ style: { fontFamily: FONT_MONO } }}
      />
      <TextField
        label={t("showcaseVideoPage.details.notifyByEmail")}
        value={d.notifyEmails}
        onChange={set("notifyEmails")}
        helperText={t("showcaseVideoPage.details.notifyHelp", {
          pct: SHOWCASE_NOTIFY_COMPLETION_PCT,
        })}
      />
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          variant="contained"
          disabled={!dirty || saving}
          onClick={async () => {
            setSaving(true);
            setError(null);
            try {
              onSaved(await showcaseApi.update(video.id, patchOf(d)));
              setSaved(true);
            } catch (e) {
              setError(errorText(e, t));
            } finally {
              setSaving(false);
            }
          }}
        >
          {t("common:buttons.save")}
        </Button>
        {saved && !dirty && (
          <Typography variant="body2" color="text.secondary">
            {t("showcaseVideoPage.details.saved")}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

export default function ShowcaseVideoPage() {
  const { t } = useTranslation(["users", "common"]);
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const uploads = useUploads();
  const upload = uploads.get(id);
  const [video, setVideo] = useState<ShowcaseVideo | null>(null);
  const [config, setConfig] = useState<ShowcaseConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"details" | "analytics" | "captions">(
    "analytics"
  );
  const [copied, setCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [posterMsg, setPosterMsg] = useState<string | null>(null);
  const previewEl = useRef<HTMLVideoElement | null>(null);
  const replaceInput = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    showcaseApi
      .get(id)
      .then(setVideo)
      .catch((e) => setError(errorText(e, t)));
  }, [id, t]);
  useEffect(() => {
    load();
    showcaseApi
      .config()
      .then(setConfig)
      .catch(() => {});
  }, [load]);

  const busy =
    video?.status === "transcoding" ||
    (upload && upload.phase !== "done" && upload.phase !== "failed");
  useEffect(() => {
    if (!busy) return;
    const t = window.setInterval(load, 5000);
    return () => window.clearInterval(t);
  }, [busy, load]);
  useEffect(() => {
    if (upload?.phase === "done") load();
  }, [upload?.phase, load]);

  useEffect(() => {
    if (
      video &&
      video.status !== "ready" &&
      tab === "analytics" &&
      video.stats.sessions === 0
    )
      setTab("details");
  }, [video, tab]);

  const onVideo = useCallback((v: HTMLVideoElement | null) => {
    previewEl.current = v;
  }, []);
  /** Caption lines and chapters jump the preview there. */
  const seekPreview = useCallback((t: number) => {
    const v = previewEl.current;
    if (!v) return;
    v.currentTime = t;
    void v.play().catch(() => {});
    v.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, []);

  if (error)
    return (
      <PageContainer>
        <Alert severity="error">{error}</Alert>
      </PageContainer>
    );
  if (!video)
    return (
      <PageContainer>
        <LinearProgress />
      </PageContainer>
    );

  const link = `${config?.publicBaseUrl ?? "https://video.nenda.com"}/v/${video.slug}`;
  const uploading =
    upload && upload.phase !== "done" && upload.phase !== "failed";

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("showcaseVideoPage.eyebrow")}
        title={video.title}
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => replaceInput.current?.click()}
              disabled={!!uploading}
            >
              {video.hlsUrl
                ? t("showcaseVideoPage.replaceVideo")
                : t("showcaseVideoPage.uploadVideo")}
            </Button>
            <Tooltip title={t("common:buttons.delete")}>
              <IconButton onClick={() => setConfirmDelete(true)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
            <input
              ref={replaceInput}
              type="file"
              accept="video/*"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) startUpload(video.id, f, load);
                e.target.value = "";
              }}
            />
          </Stack>
        }
      />

      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: {
            xs: "1fr",
            lg: "minmax(0, 1.4fr) minmax(0, 1fr)",
          },
          alignItems: "start",
        }}
      >
        <Stack spacing={1.5}>
          {video.hlsUrl ? (
            <Preview
              src={video.hlsUrl}
              poster={video.posterUrl}
              onVideo={onVideo}
            />
          ) : (
            <Box
              sx={{
                aspectRatio: "16 / 9",
                borderRadius: 3,
                bgcolor: "#0b0c12",
                display: "grid",
                placeItems: "center",
                color: "#fff",
                p: 3,
                textAlign: "center",
                backgroundImage: video.posterUrl
                  ? `linear-gradient(rgba(0,0,0,.55), rgba(0,0,0,.55)), url(${video.posterUrl})`
                  : undefined,
                backgroundSize: "cover",
              }}
            >
              <Stack spacing={1.5} alignItems="center" sx={{ width: "70%" }}>
                {uploading ? (
                  <>
                    <Typography fontWeight="fontWeightSemibold">
                      {upload!.phase === "measuring"
                        ? t("showcasePage.status.preparing")
                        : upload!.phase === "finishing"
                          ? t("showcasePage.status.finishing")
                          : t("showcaseVideoPage.uploadingFile", {
                              file: upload!.fileName,
                            })}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={upload!.progress * 100}
                      sx={{ width: "100%", height: 6, borderRadius: 3 }}
                    />
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {t("showcaseVideoPage.keepWorking", {
                        percent: Math.round(upload!.progress * 100),
                      })}
                    </Typography>
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => cancelUpload(video.id)}
                    >
                      {t("common:buttons.cancel")}
                    </Button>
                  </>
                ) : video.status === "transcoding" ? (
                  <>
                    <Typography fontWeight="fontWeightSemibold">
                      {t("showcaseVideoPage.processingWeb")}
                    </Typography>
                    <LinearProgress
                      sx={{ width: "100%", height: 6, borderRadius: 3 }}
                    />
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {t("showcaseVideoPage.buildingRenditions")}
                    </Typography>
                  </>
                ) : (
                  <Typography>
                    {video.status === "failed"
                      ? t("showcaseVideoPage.processingFailed")
                      : t("showcaseVideoPage.noVideoUploaded")}
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
          {upload?.phase === "failed" && (
            <Alert severity="error">
              {t("showcaseVideoPage.uploadFailedReason", {
                reason: upload.error,
              })}
            </Alert>
          )}
          {video.error && (
            <Alert severity={video.hlsUrl ? "warning" : "error"}>
              {video.error}
            </Alert>
          )}
          {video.hlsUrl && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                onClick={async () => {
                  const v = previewEl.current;
                  const poster = v ? posterFromElement(v) : null;
                  if (!poster) {
                    setPosterMsg(t("showcaseVideoPage.couldNotGrabFrame"));
                    return;
                  }
                  setVideo(await showcaseApi.setPoster(video.id, poster));
                  setPosterMsg(t("showcaseVideoPage.posterUpdated"));
                }}
              >
                {t("showcaseVideoPage.useFrameAsPoster")}
              </Button>
              {posterMsg && (
                <Typography variant="body2" color="text.secondary">
                  {posterMsg}
                </Typography>
              )}
            </Stack>
          )}
        </Stack>

        <Stack spacing={2}>
          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent>
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  {statusChip(t, video, upload)}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={video.published}
                        disabled={!video.hlsUrl}
                        onChange={async (e) =>
                          setVideo(
                            await showcaseApi.update(video.id, {
                              published: e.target.checked,
                            })
                          )
                        }
                      />
                    }
                    label={
                      video.published
                        ? t("showcaseVideoPage.linkLive")
                        : t("showcaseVideoPage.linkOff")
                    }
                  />
                </Stack>
                <TextField
                  value={link}
                  size="small"
                  label={t("showcaseVideoPage.privateLink")}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title={
                            copied
                              ? t("showcaseVideoPage.embed.copied")
                              : t("common:buttons.copy")
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => {
                              void navigator.clipboard.writeText(link);
                              setCopied(true);
                              window.setTimeout(() => setCopied(false), 1500);
                            }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("common:buttons.open")}>
                          <IconButton
                            size="small"
                            component="a"
                            href={link}
                            target="_blank"
                            rel="noopener"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  <Trans
                    t={t}
                    i18nKey="showcaseVideoPage.anyoneWithLink"
                    values={{ link }}
                    components={{ code: <code /> }}
                  />
                </Typography>
                <EmbedCode
                  video={video}
                  baseUrl={config?.publicBaseUrl ?? "https://video.nenda.com"}
                />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {video.durationSeconds ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={formatTimecode(video.durationSeconds)}
                    />
                  ) : null}
                  {video.width && video.height ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${video.width}×${video.height}`}
                    />
                  ) : null}
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t("showcaseVideoPage.viewsCount", {
                      count: video.stats.sessions,
                    })}
                  />
                  {video.captionStatus === "review" &&
                    video.captions.length === 0 && (
                      <Chip
                        size="small"
                        color="info"
                        label={t("showcaseVideoPage.captionsToReview")}
                        onClick={() => setTab("captions")}
                      />
                    )}
                  {(video.captionStatus === "queued" ||
                    video.captionStatus === "running") && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t("showcaseVideoPage.makingCaptions")}
                    />
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>

      <Box>
        <Tabs value={tab} onChange={(_, t) => setTab(t)} sx={{ mb: 2 }}>
          <Tab
            value="analytics"
            label={t("showcaseVideoPage.tabs.analytics")}
          />
          <Tab value="details" label={t("showcaseVideoPage.tabs.details")} />
          <Tab
            value="captions"
            label={t("showcaseVideoPage.tabs.captions")}
            disabled={!video.hlsUrl}
            data-testid="captions-tab"
          />
        </Tabs>
        {tab === "analytics" ? (
          <AnalyticsTab video={video} />
        ) : tab === "captions" ? (
          <CaptionsTab video={video} onVideo={setVideo} onSeek={seekPreview} />
        ) : (
          <DetailsTab video={video} onSaved={setVideo} />
        )}
      </Box>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>
          {t("showcaseVideoPage.deleteTitle", { title: video.title })}
        </DialogTitle>
        <DialogContent>
          <Typography>{t("showcaseVideoPage.deleteBody")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              await showcaseApi.remove(video.id);
              navigate("/video");
            }}
          >
            {t("common:buttons.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
